/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/** @jsxFrag */
import React from 'react';

/* eslint-disable @atlaskit/design-system/no-css-tagged-template-expression */
// See https://hello.atlassian.net/wiki/spaces/EDAI/pages/4834594169/LDR+Keeping+Emotion+Global+usages
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, keyframes, Global, jsx } from '@emotion/react';

import { SelectionStyle } from '@atlaskit/editor-shared-styles/selection/types';
import { getSelectionStyles } from '@atlaskit/editor-shared-styles/selection/utils';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/editor-experiment';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { token } from '@atlaskit/tokens';

import { InlineSuggestionToolbar } from '../../../pm-plugins/inline-suggestions/floating-toolbar/floating-toolbar';
import { SuggestionManager } from '../../../pm-plugins/inline-suggestions/handlers/suggestion-manager';

const highlightStyles = css`
	background-color: ${token('color.background.accent.blue.subtlest')};
	border-bottom: ${token('border.width.selected')} solid
		${token('color.background.accent.blue.subtler')};
`;

const nodeHighlightStyles = css`
	border: ${token('border.width')} solid ${token('color.background.accent.blue.subtle')};
	box-shadow: 0 0 2px 1px ${token('color.background.accent.blue.subtler')};
`;

const cellHighlightStyles = css`
	${nodeHighlightStyles}
	::after {
		border-left: ${token('border.width')} solid ${token('color.background.accent.blue.subtle')};
		border-bottom: ${token('border.width')} solid ${token('color.background.accent.blue.subtle')};
	}
`;

// override standard dropdown max-height 90vh to accomodate for long list of translation languages
const translationsFloatingToolbarDropdownStyles = css`
	[aria-label='Annotation toolbar'],
	[aria-label='Selection toolbar'] {
		[data-role='droplistContent'] {
			max-height: 400px;
			overflow-x: hidden;
			[data-testid^='ak-editor-ai-selection-toolbar-translate-button']
				[data-item-elem-before]
				> span {
				padding: 0;
			}
		}
		[data-testid='ak-editor-ai-selection-toolbar-ai-dropdown'] > span > span {
			/* override negative margin on dropdown button */
			margin: 0;
		}
	}
`;

const dropdownHeadingStyles = css`
	[aria-label='Selection toolbar'] {
		[data-testid^='ak-editor-ai-selection-toolbar-dropdown-heading'] {
			cursor: default;
			> span {
				gap: initial;
			}
			[data-item-elem-before] > span {
				padding: 0;
			}

			[data-item-title] {
				text-transform: uppercase;
				font-size: 0.75rem;
				font-weight: ${token('font.weight.bold')};
				color: ${token('color.text')};
			}
		}
		[data-testid^='ak-editor-ai-selection-toolbar-dropdown-heading']:hover {
			color: ${token('color.text')};
		}
	}
`;

const rovoAgentsFloatingToolbarDropdownStyles = css`
	[aria-label='Annotation toolbar'],
	[aria-label='Selection toolbar'] {
		[data-role='droplistContent'] {
			[data-testid='ak-editor-ai-selection-toolbar-dropdown-view-all-agents']:not(:only-child) {
				border-block-start: ${token('border.width.selected')} solid
					var(--ds-menu-seperator-color, ${token('color.border')});
				> span {
					gap: 4px;
				}
			}

			button[data-testid^='ak-editor-ai-selection-toolbar-dropdown-agent']
				[data-item-title='true']
				> span {
				white-space: normal;
			}
		}
	}
`;

const aiSelectionToolbarButtonStyles = () => {
	if (editorExperiment('platform_editor_controls', 'variant1')) {
		// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression
		return css`
			/* Reduce ai button padding as per designs */
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'],
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:disabled,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:hover,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:focus {
				padding: 0px ${token('space.075')};
			}

			/* Reduce annotation button padding as per designs */
			button[data-testid='ak-editor-annotation-toolbar-create-button'],
			button[data-testid='ak-editor-annotation-toolbar-create-button']:disabled,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:hover,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:focus {
				padding: 0px ${token('space.075')};
				span:first-child {
					margin: 0;
				}
				span[role='img'],
				svg {
					padding: 0;
				}
			}
		`;
	} else {
		return css`
			/* Reduce ai button padding as per designs */
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'],
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:disabled,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:hover,
			button[data-testid='ak-editor-ai-selection-toolbar-ai-button']:focus,
			/* Reduce annotation button padding as per designs */
			button[data-testid='ak-editor-annotation-toolbar-create-button'],
			button[data-testid='ak-editor-annotation-toolbar-create-button']:disabled,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:hover,
			button[data-testid='ak-editor-annotation-toolbar-create-button']:focus {
				padding: 0px ${token('space.100')};
			}

			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'] svg {
				padding-right: ${token('space.050')};
			}

			button[data-testid='ak-editor-ai-selection-toolbar-ai-button'] > span:only-child {
				svg {
					padding: 0px ${token('space.100')};
				}
			}
		`;
	}
};

const inlineSuggestionsStyles = () => {
	return css`
		.ak-editor-content-area .${InlineSuggestionToolbar.INLINE_SUGGESTION_FLOATING_TOOLBAR_CLASS} {
			padding: ${token('space.100')};
			border-radius: ${token('space.075')};

			/* Reset default margin on date lozenge elements to ensure padding is applied as intended */
			[class$='-DateLozenge'] {
				margin: 0;
			}
		}
		.${SuggestionManager.INLINE_SUGGESTION_DECORATION_CLASSES.underline} {
			text-decoration-line: underline;
			text-decoration-style: dashed;
			text-decoration-color: ${token('color.border.accent.blue')};
		}

		.${SuggestionManager.INLINE_SUGGESTION_DECORATION_CLASSES.highlight} {
			background-color: ${token('color.background.selected')};
		}

		.${SuggestionManager.INLINE_SUGGESTION_DECORATION_CLASSES.completionHint} {
			color: ${token('color.text')};
			opacity: ${token('opacity.disabled')};
			pointer-events: none;
			user-select: none;
		}
	`;
};

const userHighlightBackgroundColor = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'.akEditor div.ProseMirror': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.ai-pre-stream-range-selection': {
			backgroundColor: 'Highlight',
			color: 'HighlightText',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'&.ak-editor-annotation-draft, &.ak-editor-annotation-focus, &.ak-editor-annotation-blur': {
				all: 'unset',
				backgroundColor: 'Highlight',
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.annotationView-content-wrap': {
			color: 'HighlightText',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'.ak-editor-annotation-draft, .ak-editor-annotation-focus, .ak-editor-annotation-blur': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
				'&:has(.ai-pre-stream-selection-highlight)': {
					all: 'unset',
					backgroundColor: 'Highlight',
				},
			},
		},
	},
});

const inEditorStreamingStyles = css`
	.akEditor div.ProseMirror {
		.ai-modal-mount-point-widget {
			bottom: 0;
		}
		tr > .ai-modal-mount-point-widget {
			display: table-cell;
		}
		.ai-pre-stream-empty-selection-caret {
			content: '';
			position: relative;
			border-left: ${token('border.width')} solid ${token('color.text')};
			margin-left: -1px;
			left: 0.5px;
		}
		.ai-inline-streaming-hidden {
			display: none;
		}
		.ai-in-editor-user-content {
			opacity: 0.2;
		}
		.ai-in-editor-ai-content:not(
			.ak-editor-annotation-draft,
			.ak-editor-annotation-focus,
			.ak-editor-annotation-blur
		) {
			background-color: var(
				--agent-edit-chrome-background-color,
				${token('color.background.accent.purple.subtlest')}
			);
			/* The agent telepointer redesign suppresses this per-decoration (its Figma highlight
			   has no underline) via an inline border-bottom-width: 0 in
			   getAIContentDecorationAttrs, so it only applies to non-agent streaming and the
			   collab agent-edit-chrome path. */
			border-bottom-color: var(
				--agent-edit-chrome-border-color,
				${token('color.border.accent.purple')}
			);
			border-bottom-style: dashed;
			border-bottom-width: ${token('border.width')};
		}
		.ai-in-editor-telepointer {
			position: relative;
			z-index: 1;
			& > .ai-in-editor-telepointer-avatar {
				position: absolute;
				/* Lift the 20px pill a full badge-height above the caret so it sits one line above the
				   text, with the ::after cursor line dropping from the pill down into the text. Matches
				   the Editor Telepointer Figma spec (node 3445:474838) and the collaborator telepointer,
				   which elevates its badge the same way. */
				top: -20px;
				left: ${token('space.0')};
			}
			.ai-in-editor-telepointer-badge {
				box-sizing: border-box;
				display: flex;
				flex-direction: row;
				align-items: center;
				gap: ${token('space.050')};
				height: 20px;
				/* Asymmetric by design: the badge butts against the cursor line on its left. */
				padding: ${token('space.025')} ${token('space.050')} ${token('space.025')} 5px;
				border-radius: 0 ${token('space.050')} ${token('space.050')} 0;
				background: var(
					--agent-edit-chrome-telepointer-line-color,
					${token('color.background.accent.purple.bolder')}
				);
				font-family: ${token('font.family.body')};
				font-weight: ${token('font.weight.regular')};
				font-size: 12px;
				line-height: 16px;
				white-space: pre;
				color: ${token('color.text.inverse')};

				/* Every badge puts its leading glyph or avatar in this centred slot so the icon sits
				   identically across types. Glyph icons are 12px (Figma spec); the child svg fills
				   the slot, which also centres the Rovo hex (its IconWrapper span would otherwise
				   leave it top-aligned). */
				.ai-in-editor-telepointer-badge-leading {
					display: inline-flex;
					align-items: center;
					justify-content: center;
					flex: none;
					width: 12px;
					height: 12px;
				}
				/* Glyph icons: force whatever wrapper the icon renders (e.g. the logo IconWrapper
				   span, sized 16px by default) to fill and centre in the 12px slot, so its own
				   intrinsic size can't leak out. Excludes the avatar, which manages its own box. */
				.ai-in-editor-telepointer-badge-leading:not(.ai-in-editor-telepointer-badge-leading-avatar)
					> * {
					display: flex;
					align-items: center;
					justify-content: center;
					width: 100%;
					height: 100%;
				}
				.ai-in-editor-telepointer-badge-leading svg {
					width: 100%;
					height: 100%;
				}
				/* Avatars render one size up from the glyph icons (Figma), and AgentAvatar's minimum
				   is 16px, so the avatar slot is 16px. */
				.ai-in-editor-telepointer-badge-leading-avatar {
					width: 16px;
					height: 16px;
				}
			}
			/* First party is an avatar-only square: 16px avatar + 2px padding all round = 20x20,
			   matching the Figma "Agents no shape" badge, rather than the wider labelled padding. */
			.ai-in-editor-telepointer-badge.ai-in-editor-telepointer-badge-icon-only {
				padding: ${token('space.025')};
			}
			/* The neutral badge is too light to carry inverse text. */
			.ai-in-editor-telepointer-badge-neutral {
				color: ${token('color.text')};
			}
			& > .ai-in-editor-telepointer-label {
				position: absolute;
				box-sizing: border-box;
				display: flex;
				flex-direction: row;
				justify-content: center;
				align-items: center;
				padding: ${token('space.025')};
				gap: ${token('space.075')};
				height: 16px;
				top: ${token('space.0')};
				left: ${token('space.0')};
				border-radius: 0px ${token('space.050')} ${token('space.050')} 0px;
				border: solid ${token('border.width')} transparent;
				background:
					linear-gradient(${token('color.text')}, ${token('color.text')}) padding-box,
					conic-gradient(
							from 270deg at 50% 50%,
							#1868db 0deg,
							#1868db 115deg,
							#fca700 115deg,
							#fca700 180deg,
							#bf63f3 180deg,
							#bf63f3 310deg,
							#82b536 310deg,
							#82b536 359.96deg,
							#1868db 360deg
						)
						border-box;

				font-family: ${token('font.family.body')};
				font-weight: ${token('font.weight.semibold')};
				font-size: 10px;
				line-height: 9px;
				text-align: center;
				white-space: pre;
				color: ${token('color.text.inverse')};
			}
			&::after {
				content: '';
				position: absolute;
				/* Width, height and colour all default to the pre-redesign line (1px, 24px,
				   border.brand), so the off-experiment telepointer and the collab agent-edit-chrome
				   line are unchanged. The redesign badge updater sets these three variables together
				   only when it renders the badge: a 2px line the height of the content highlight, in
				   the agent's own hue. The Figma mock draws 1px, but a hairline barely reads as the
				   hue next to the 2px highlight underline, so the redesign line matches that weight. */
				width: var(--agent-edit-chrome-telepointer-line-width, 1px);
				height: var(--agent-edit-chrome-telepointer-line-height, 24px);
				top: ${token('space.0')};
				left: ${token('space.0')};
				background: var(--agent-edit-chrome-telepointer-line-color, ${token('color.border.brand')});
			}
		}
	}
`;

/**
 * We position it as a table row to ensure it doesn't affect the table layout.
 */
const modalMountPointTableFix = css`
	.akEditor div.ProseMirror {
		tbody > .ai-modal-mount-point-widget {
			height: 0;
			display: table-row;
			border-top-style: solid;
			border-top-width: ${token('border.width')};
		}
	}
`;

const globalStyles = () => {
	return css`
		.akEditor div.ProseMirror {
			.ai-modal-start-widget,
			.ai-modal-end-widget {
				position: relative;

				&::before {
					content: '';
					background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMyIgaGVpZ2h0PSIyMCIgdmlld0JveD0iMCAwIDMgMjAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMSAxSDBMMSAxLjg1NzE0VjE4LjE0MzNMMCAxOS4wMDA0SDNMMiAxOC4xNDMzVjEuODU3MTRMMyAxSDJIMVoiIGZpbGw9IiM1NzlERkYiLz4KPHJlY3QgeT0iMTkiIHdpZHRoPSIzIiBoZWlnaHQ9IjEiIGZpbGw9IiM1NzlERkYiLz4KPHJlY3Qgd2lkdGg9IjMiIGhlaWdodD0iMSIgZmlsbD0iIzU3OURGRiIvPgo8L3N2Zz4K');
					z-index: 1;
					display: block;
					position: absolute;
					top: 0;
					bottom: ${token('space.negative.025')}; /* match highlight height + border */
					background-repeat: no-repeat;
					background-position-x: center;
					background-position-y: center;
					background-size: contain;
					aspect-ratio: 3/20; /* svg (width / height) */
				}
			}

			.ai-modal-start-widget::before {
				left: 0px;
				margin-left: -0.1em;
			}

			.ai-modal-end-widget::before {
				right: 0px;
				margin-right: -0.1em;
			}

			.ai-generated-content-highlight {
				${highlightStyles}
			}

			.ai-selection-highlight {
				${highlightStyles}

				/* selection from the right */
				&.ak-editor-annotation-draft,
				&.ak-editor-annotation-focus,
      			&.ak-editor-annotation-blur {
					all: unset;

					${highlightStyles}
				}
			}

			.ai-selection-node .pm-table-wrapper {
				table tr td,
				table tr th {
					${getSelectionStyles([SelectionStyle.Border, SelectionStyle.Blanket])}
				}
			}

			.ai-generated-node-highlight .pm-table-wrapper {
				table tr td,
				table tr th {
					${cellHighlightStyles}
				}
			}

			// selection from the left
			.annotationView-content-wrap {
				.ak-editor-annotation-draft,
				.ak-editor-annotation-focus,
				.ak-editor-annotation-blur {
					&:has(.ai-selection-highlight) {
						all: unset;

						${highlightStyles}
					}
				}
			}
		}
	`;
};

const nestedEditorStyles = css({
	// use multiple class here to ensure we have the highest specificity
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.ak-editor-plugin-ai-modal .ak-editor-content-area .ProseMirror': {
		/**
		 * Minimum height is set for confluence comment editor
		 * when Editor AI is invoked in comments. So overriding it here.
		 */
		minHeight: 'auto',

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		'> p:last-of-type': {
			marginBottom: 0,
		},
	},
});

const aiCaretBlink = keyframes({
	'from, to': {
		opacity: 0,
	},
	'50%': {
		opacity: 1,
	},
});

const aiQuickPromptStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.ProseMirror .placeholder-decoration.placeholder-decoration-inline.ai-quick-prompt-placeholder-decoration':
		{
			display: 'inline-flex',
			alignItems: 'center',
			width: 'auto',
			verticalAlign: 'middle',
		},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'.ProseMirror:has(.ai-quick-prompt-placeholder-decoration)': {
		caretColor: 'transparent',
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.ProseMirror .ai-quick-prompt-caret': {
		display: 'inline-block',
		alignSelf: 'stretch',
		width: '1px',
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
		marginLeft: '-0.5px',
		animation: `1s ${aiCaretBlink} step-start infinite`,
		background: `linear-gradient(to top,
				#FCA700 0%,
				#FCA700 20.2%,
				#6A9A23 20.2%,
				#6A9A23 46.6%,
				#1868DB 46.6%,
				#1868DB 70.2%,
				#AF59E0 70.2%,
				#AF59E0 100%)`,
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.ProseMirror .ai-quick-prompt-code': {
		display: 'inline-block',
		borderRadius: token('radius.xsmall'),
		border: `1px solid ${token('color.border.bold')}`,
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.ProseMirror .ai-quick-prompt-message': {
		display: 'inline-block',
		marginLeft: token('space.025'),
		paddingLeft: token('space.025'),
		paddingRight: token('space.050'),
		paddingTop: token('space.025'),
		paddingBottom: token('space.025'),
		borderRadius: token('radius.small'),
		color: token('color.text.subtle'),
		backgroundColor: token('color.background.neutral'),
		font: token('font.body.small'),
	},
});

export const GlobalWrapper = ({ children }: { children: React.ReactNode }): JSX.Element => {
	return (
		<React.Fragment>
			{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766 */}
			<Global
				// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
				styles={[
					translationsFloatingToolbarDropdownStyles,
					rovoAgentsFloatingToolbarDropdownStyles,
					inlineSuggestionsStyles(),
					dropdownHeadingStyles,
					globalStyles(),
					inEditorStreamingStyles,
					userHighlightBackgroundColor,
					aiSelectionToolbarButtonStyles(),
					nestedEditorStyles,
					modalMountPointTableFix,
					expValEqualsNoExposure('platform_editor_ai_quick_prompt_iw_cc', 'isEnabled', true) &&
						aiQuickPromptStyles,
				]}
			/>
			{children}
		</React.Fragment>
	);
};
